<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { storeToRefs } from 'pinia';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SectionLayout from '../../account/components/SectionLayout.vue';
import FeatureToggle from '../components/FeatureToggle.vue';
import ModelSelector from '../components/ModelSelector.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  copilotConfig: { type: Object, required: true },
});

const emit = defineEmits(['featureToggle', 'modelChange', 'configChange']);

const { t } = useI18n();
const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatwootCloud } = useAccount();

const captainConfigStore = useCaptainConfigStore();
const { features } = storeToRefs(captainConfigStore);

const isCopilotEnabled = computed(
  () => features.value?.copilot?.enabled ?? false
);

const isFeatureAccessible = () => {
  if (isOnChatwootCloud.value) return true;
  return isEnterprise && enterprisePlanName === 'enterprise';
};

const handleFeatureToggle = ({ feature, enabled }) => {
  emit('featureToggle', { feature, enabled });
};

const handleModelChange = ({ feature, model }) => {
  emit('modelChange', { feature, model });
};

const updateConfigField = (field, value) => {
  emit('configChange', {
    section: 'copilot',
    data: { [field]: value },
  });
};

const TONE_OPTIONS = [
  { value: 'professional', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.TONE.PROFESSIONAL' },
  { value: 'casual', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.TONE.CASUAL' },
  { value: 'technical', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.TONE.TECHNICAL' },
];

const LANGUAGE_OPTIONS = [
  { value: 'pt-BR', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.LANGUAGE.PT_BR' },
  { value: 'en', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.LANGUAGE.EN' },
  { value: 'es', labelKey: 'CAPTAIN_SETTINGS.COPILOT_CONFIG.LANGUAGE.ES' },
];

const currentTone = computed(() => props.copilotConfig.tone || 'professional');
const currentLanguage = computed(() => props.copilotConfig.language || 'pt-BR');
const currentMaxContext = computed(
  () => props.copilotConfig.max_context_messages ?? 10
);
const currentAutoSuggest = computed(
  () => props.copilotConfig.auto_suggest === true
);

const handleToneChange = event => {
  updateConfigField('tone', event.target.value);
};

const handleLanguageChange = event => {
  updateConfigField('language', event.target.value);
};

const handleMaxContextChange = event => {
  const val = parseInt(event.target.value, 10);
  if (!Number.isNaN(val) && val >= 1 && val <= 50) {
    updateConfigField('max_context_messages', val);
  }
};

const handleAutoSuggestToggle = () => {
  updateConfigField('auto_suggest', !currentAutoSuggest.value);
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Description -->
    <div class="pb-2">
      <p class="text-sm text-n-slate-11 mb-0">
        {{ t('CAPTAIN_SETTINGS.TABS.COPILOT.INTRO') }}
      </p>
    </div>

    <!-- Enable/Disable Copilot -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
      :description="t('CAPTAIN_SETTINGS.TABS.COPILOT.FEATURES_DESC')"
    >
      <div class="grid gap-4">
        <FeatureToggle
          feature-key="copilot"
          :is-allowed="isFeatureAccessible()"
          @change="handleFeatureToggle"
          @model-change="handleModelChange"
        />
      </div>
    </SectionLayout>

    <!-- Model Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
      :description="t('CAPTAIN_SETTINGS.TABS.COPILOT.MODEL_DESC')"
      with-border
    >
      <ModelSelector
        :is-allowed="isFeatureAccessible()"
        feature-key="copilot"
        :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE')"
        :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION')"
        @change="handleModelChange"
      />
    </SectionLayout>

    <!-- Copilot Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.COPILOT_CONFIG.TITLE')"
      :description="t('CAPTAIN_SETTINGS.COPILOT_CONFIG.DESCRIPTION')"
      with-border
    >
      <div
        class="flex flex-col gap-4"
        :class="{ 'opacity-50 pointer-events-none': !isCopilotEnabled }"
      >
        <!-- Tone -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.COPILOT_CONFIG.TONE.LABEL') }}
          </label>
          <select
            :value="currentTone"
            class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
            @change="handleToneChange"
          >
            <option
              v-for="option in TONE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.labelKey) }}
            </option>
          </select>
        </div>

        <!-- Language -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.COPILOT_CONFIG.LANGUAGE.LABEL') }}
          </label>
          <select
            :value="currentLanguage"
            class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
            @change="handleLanguageChange"
          >
            <option
              v-for="option in LANGUAGE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.labelKey) }}
            </option>
          </select>
        </div>

        <!-- Max Context Messages -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.COPILOT_CONFIG.MAX_CONTEXT.LABEL') }}
          </label>
          <input
            type="number"
            :value="currentMaxContext"
            min="1"
            max="50"
            class="block w-full h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 placeholder:text-n-slate-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            @input="handleMaxContextChange"
          />
          <p class="text-xs text-n-slate-11 mb-0">
            {{ t('CAPTAIN_SETTINGS.COPILOT_CONFIG.MAX_CONTEXT.HELP') }}
          </p>
        </div>

        <!-- Auto-Suggest Toggle -->
        <div
          class="flex items-center justify-between gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CAPTAIN_SETTINGS.COPILOT_CONFIG.AUTO_SUGGEST.LABEL') }}
            </h4>
            <p class="text-sm text-n-slate-11 mt-0.5 mb-0">
              {{
                currentAutoSuggest
                  ? t('CAPTAIN_SETTINGS.COPILOT_CONFIG.AUTO_SUGGEST.ON')
                  : t('CAPTAIN_SETTINGS.COPILOT_CONFIG.AUTO_SUGGEST.OFF')
              }}
            </p>
          </div>
          <Switch
            :model-value="currentAutoSuggest"
            @change="handleAutoSuggestToggle"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
